import { useTranslation } from '@rhino-project/core/i18n';
import { useEffect, useMemo } from 'react';
import { z } from 'zod';

import { useResolver } from '@rhino-project/core/hooks';
import { SubmitButton } from '../buttons';
import { FormProvider } from '@rhino-project/core/components/forms';
import { useForm, useFormState, type FieldValues } from 'react-hook-form';
import { FieldInput } from '../../FieldInput';
import { FieldPassword } from '../../Field';
import {
  Alert,
  AlertContent,
  AlertTitle,
  Fieldset,
  FieldGroup,
  FieldsetActions,
  Form
} from '@heroui/react';
import { Link, useSearch } from '@tanstack/react-router';
import type { FieldInputProps } from '../../FieldInput';

interface SecondaryAction {
  content: string;
  url: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AuthErrors = Record<string, any> | string[] | null;

export type AuthFormField =
  | 'email'
  | 'name'
  | 'current_password'
  | 'password'
  | 'password_confirmation'
  | 'organization';

export type AuthFormValues = Partial<Record<AuthFormField, string>>;

export interface AuthFormProps {
  emailField?: boolean;
  nameField?: boolean;
  currentPasswordField?: boolean;
  organizationField?: boolean;
  passwordField?: boolean;
  passwordConfirmField?: boolean;
  // Add the "webauthn" autocomplete token to the email field so passkeys surface
  // inline via conditional UI (browser autofill).
  webauthnAutofill?: boolean;
  primaryAction: string;
  secondaryAction?: SecondaryAction;
  isPending?: boolean;
  errors?: AuthErrors;
  onSubmit: (formValues: AuthFormValues) => void;
}

const AuthField = (props: FieldInputProps) => {
  const { t } = useTranslation();
  return (
    <FieldInput
      placeholder={t('rhino.frontend.messages.email')}
      variant="secondary"
      {...props}
    />
  );
};
const AuthFieldPassword = (props: FieldInputProps) => {
  const { t } = useTranslation();
  return (
    <FieldPassword
      placeholder={t('rhino.frontend.messages.password')}
      variant="secondary"
      {...props}
    />
  );
};

export const AuthForm = ({
  emailField = false,
  nameField = false,
  currentPasswordField = false,
  organizationField = false,
  passwordField = false,
  passwordConfirmField = false,
  webauthnAutofill = false,
  primaryAction,
  secondaryAction,
  isPending = false,
  errors,
  onSubmit
}: AuthFormProps) => {
  const { t } = useTranslation();
  const { error: omniError } = useSearch({ strict: false }) as {
    error?: string;
  };

  // Autocomplete attributes to match Chrome standards
  // https://www.chromium.org/developers/design-documents/form-styles-that-chromium-understands/
  const passwordDescriptor = useMemo(
    () =>
      t(
        currentPasswordField
          ? 'rhino.frontend.messages.new_password'
          : 'rhino.frontend.messages.password'
      ),
    [currentPasswordField, t]
  );

  const passwordComplete = useMemo(
    () => `${passwordConfirmField ? 'new' : 'current'}-password`,
    [passwordConfirmField]
  );

  // Unused fields can't be set or they will cause errors
  const { schema, defaultValues } = useMemo(() => {
    const shape: Record<string, z.ZodType> = {};
    const defaults: Record<string, string> = {};
    // These auth fields don't map to a Rhino model, so their messages are
    // hand-built (not the Rails-baked messages used by generated model forms).
    // Mirror yup's "<label> is a required field" wording so the copy is stable.
    const reqString = (label: string) =>
      z.string().min(1, {
        error: t('rhino.frontend.messages.required_field', { label })
      });

    if (emailField) {
      // Required must win over format when blank: check non-empty first, then
      // pipe to the email format so '' reports the required message, not the
      // format error. z.email() as the base would run the format check first.
      shape.email = z
        .string()
        .min(1, {
          error: t('rhino.frontend.messages.email_is_a_required_field')
        })
        .pipe(
          z.email({
            error: t('rhino.frontend.messages.email_must_be_a_valid_email')
          })
        );
      defaults.email = '';
    }
    if (nameField) {
      shape.name = reqString(t('rhino.frontend.messages.name'));
      defaults.name = '';
    }
    if (currentPasswordField) {
      shape.current_password = reqString(passwordDescriptor);
      defaults.current_password = '';
    }
    if (passwordField) {
      shape.password = reqString(passwordDescriptor);
      defaults.password = '';
    }
    if (passwordConfirmField) {
      shape.password_confirmation = reqString(
        t('rhino.frontend.messages.confirm_password')
      );
      defaults.password_confirmation = '';
    }
    if (organizationField) {
      shape.organization = z.string().default('');
      defaults.organization = '';
    }

    let built: z.ZodType = z.object(shape);
    if (passwordConfirmField) {
      built = (built as z.ZodObject<z.ZodRawShape>).refine(
        (v) => v.password === v.password_confirmation,
        {
          error: t('rhino.frontend.messages.passwords_must_match'),
          path: ['password_confirmation']
        }
      );
    }

    return { schema: built, defaultValues: defaults };
  }, [
    emailField,
    nameField,
    currentPasswordField,
    passwordField,
    passwordConfirmField,
    organizationField,
    t,
    passwordDescriptor
  ]);

  const resolver = useResolver(schema);

  // Just use the first error for each field
  const reducedErrors = useMemo(() => {
    if (!errors) return undefined;

    return Object.keys(errors).reduce(
      (errorObj: Record<string, { type: string; message: string }>, name) => {
        errorObj[name] = {
          type: 'manual',
          message: (errors as Record<string, string[]>)[name][0]
        };
        return errorObj;
      },
      {}
    );
  }, [errors]);

  const methods = useForm<FieldValues>({
    defaultValues,
    disabled: isPending,
    errors: reducedErrors,
    mode: 'onBlur',
    resolver
  });
  const { handleSubmit, setFocus } = methods;
  // The password field's label is rendered as a custom row (with the
  // secondary-action link) outside the react-aria TextField, so it doesn't
  // inherit the field's data-invalid styling. Subscribe to the password error
  // to colour that label like a normal invalid label.
  const { errors: liveErrors } = useFormState({ control: methods.control });

  // Autofocus the first relevant field on mount. shouldSelect selects any
  // prefilled text so the user can type over it.
  useEffect(() => {
    const focusOptions = { shouldSelect: true };

    if (currentPasswordField) {
      setFocus('current_password', focusOptions);
    } else if (emailField) {
      setFocus('email', focusOptions);
    } else if (passwordField) {
      setFocus('password', focusOptions);
    }
  }, [currentPasswordField, emailField, passwordField, setFocus]);

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Fieldset>
          <FieldGroup>
            {currentPasswordField && (
              <AuthFieldPassword
                path="current_password"
                label={t('rhino.frontend.messages.current_password')}
                autoComplete="current-password"
              />
            )}
            {nameField && (
              <AuthField
                path="name"
                label={t('rhino.frontend.messages.name')}
                placeholder={t('rhino.frontend.messages.name')}
                autoComplete="name"
              />
            )}
            {emailField && (
              <AuthField
                path="email"
                label={t('rhino.frontend.messages.email')}
                autoComplete={
                  webauthnAutofill ? 'username webauthn' : 'username'
                }
                type="email"
              />
            )}
            {passwordField && (
              <div>
                {secondaryAction && (
                  <div className="flex justify-between items-center mb-1">
                    <span
                      className={`label${
                        liveErrors.password ? ' label--invalid' : ''
                      }`}
                    >
                      {passwordDescriptor}
                    </span>
                    <Link
                      className="text-sm text-accent hover:underline"
                      to={secondaryAction.url}
                    >
                      {secondaryAction.content}
                    </Link>
                  </div>
                )}
                <AuthFieldPassword
                  path="password"
                  label={secondaryAction ? undefined : passwordDescriptor}
                  autoComplete={passwordComplete}
                />
              </div>
            )}
            {passwordConfirmField && (
              <AuthFieldPassword
                path="password_confirmation"
                label={t('rhino.frontend.messages.confirm_value', {
                  value1: passwordDescriptor
                })}
                autoComplete={passwordComplete}
              />
            )}
            {organizationField && (
              <AuthField
                path="organization"
                label={t('rhino.frontend.messages.organization')}
                placeholder={t('rhino.frontend.messages.organization')}
                autoComplete="organizaton"
                required={false}
              />
            )}
          </FieldGroup>

          {(Array.isArray(errors) || omniError) && (
            <Alert
              status="danger"
              className="bg-danger/10 border border-danger/20"
            >
              <AlertContent>
                <AlertTitle>{omniError || (errors as string[])[0]}</AlertTitle>
              </AlertContent>
            </Alert>
          )}

          <FieldsetActions className="w-full">
            <div className="flex flex-col gap-2 w-full">
              <SubmitButton isPending={isPending}>{primaryAction}</SubmitButton>
              {secondaryAction && !passwordField && (
                <p className="text-sm text-muted text-center">
                  <Link
                    className="text-accent hover:underline"
                    to={secondaryAction.url}
                  >
                    {secondaryAction.content}
                  </Link>
                </p>
              )}
            </div>
          </FieldsetActions>
        </Fieldset>
      </Form>
    </FormProvider>
  );
};
